import React, { useRef, useEffect, useState } from 'react';
import { Shield, AlertCircle } from 'lucide-react';
import { useScan } from '../contexts/ScanContext';

const LONG_SCAN_WARNING_TIMEOUT_MS = 300000; // 5 minutes

const ScanAnimation = (props = {}) => {
  // Reads scan progress from ScanContext by default, but every value can be
  // overridden via props so the same animation can drive a running pentest
  // (fed from PentestContext) without being coupled to the scan context.
  // A prop that is passed explicitly wins even when it is null, so a pentest
  // with no phase or queue position yet doesn't show the last scan's values.
  const ctx = useScan();
  // With `standalone`, a prop that is omitted falls back to a neutral value instead of
  // ScanContext's, so a caller that forgets one can never show the last code scan's data.
  const pick = (key, scanValue, neutral) => {
    if (props[key] !== undefined) return props[key];
    return props.standalone ? neutral : scanValue;
  };
  const scanProgress = pick('progress', ctx.scanProgress, 0);
  const scanLogs = pick('logs', ctx.scanLogs, []);
  const repoUrl = pick('targetLabel', ctx.repoUrl, null);
  const discoveredVulnCount = pick('discoveredCount', ctx.discoveredVulnCount, 0);
  const scanStage = pick('stage', ctx.scanStage, 'scanning');
  const queuePosition = pick('queuePosition', ctx.queuePosition, null);
  const heading = props.heading ?? 'Analyzing Your Codebase';
  const subtitle = props.subtitle ?? 'Our multi-agent system is analyzing your code patterns and security implementations';
  const progressLabel = props.progressLabel ?? 'Security Scan in Progress';
  const targetPrefix = props.targetPrefix ?? 'Repository';
  const queuedHeading = props.queuedHeading ?? 'Your scan is in the queue';
  const logContainerRef = useRef(null);
  const [showLongRunningWarning, setShowLongRunningWarning] = useState(false);

  // Auto-scroll logs when new logs are added
  useEffect(() => {
    if (logContainerRef.current) {
      logContainerRef.current.scrollTop = logContainerRef.current.scrollHeight;
    }
  }, [scanLogs]);

  // Show warning for long-running scans
  useEffect(() => {
    const warningTimer = setTimeout(() => {
      if (scanProgress < 100) {
        setShowLongRunningWarning(true);
      }
    }, LONG_SCAN_WARNING_TIMEOUT_MS);

    return () => clearTimeout(warningTimer);
  }, [scanProgress]);

  if (scanStage === 'queued') {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-8">
        <div className="max-w-3xl w-full bg-white border border-gray-200 rounded-xl p-8 shadow-sm">
          <div className="flex flex-col items-center justify-center py-12 text-center">
            <div className="w-16 h-16 border-4 border-gray-200 border-t-indigo-500 rounded-full animate-spin mb-6" />
            <h3 className="text-xl font-semibold text-gray-800 mb-2">
              {queuedHeading}
            </h3>
            {queuePosition != null && queuePosition > 0 && (
              <p className="text-base font-medium text-indigo-600 mb-2">
                Position in queue: #{queuePosition}
              </p>
            )}
            {queuePosition != null && queuePosition === 0 && (
              <p className="text-base font-medium text-indigo-600 mb-2">
                You're next — starting soon
              </p>
            )}
            <p className="text-sm text-gray-500 max-w-md">
              Your scan will begin automatically when compute becomes available.
              You can leave this page and come back later.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center p-8">
      <div className="max-w-3xl w-full bg-white border border-gray-200 rounded-xl p-8 shadow-sm">
        <h2 className="text-2xl font-bold text-gray-900 mb-6 text-center">{heading}</h2>

        <div className="flex justify-center mb-6">
          <div className="relative w-48 h-48">
            <div className="absolute inset-0 bg-teal-100 rounded-full animate-pulse"></div>
            <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2">
              <Shield className="w-24 h-24 text-teal-600" />
            </div>
          </div>
        </div>

        <div className="mb-6 text-center">
          <p className="text-xl font-semibold text-gray-700 mb-2">
            {progressLabel}: {Math.round(scanProgress)}%
          </p>
          <p className="text-gray-600 text-sm mb-2">
            {subtitle}
          </p>
          {/* Point 3: Show vulnerability count during scan */}
          {discoveredVulnCount > 0 && (
            <p className="text-yellow-600 text-sm font-medium mb-2">
              {discoveredVulnCount > 100
                ? `Processing ${discoveredVulnCount}+ vulnerabilities, this may take a few minutes...`
                : `Found ${discoveredVulnCount} vulnerabilities so far...`
              }
            </p>
          )}
          {repoUrl && (
            <p className="text-gray-500 text-sm">
              {targetPrefix}: <span className="text-gray-900 font-medium">{repoUrl}</span>
            </p>
          )}
        </div>

        {/* Warning for long-running scans */}
        {showLongRunningWarning && (
          <div className="mb-4 bg-yellow-50 border border-yellow-200 rounded-lg p-4 flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-yellow-600 flex-shrink-0 mt-0.5" />
            <div className="text-sm text-yellow-800">
              <p className="font-semibold mb-1">Large scan in progress</p>
              <p>This repository has many files or vulnerabilities. Scans can take up to 15 minutes for large codebases. Your scan will continue in the background.</p>
            </div>
          </div>
        )}

        <div className="w-full bg-gray-200 rounded-full h-2 mb-6">
          <div
            className="bg-teal-500 rounded-full h-2 transition-all duration-300"
            style={{ width: `${scanProgress}%` }}
          />
        </div>

        <div
          ref={logContainerRef}
          className="bg-gray-900 rounded-lg p-4 h-48 overflow-y-auto font-mono text-sm text-gray-100"
        >
          {scanLogs.map((log, index) => (
            <div
              key={index}
              className="mb-2 flex items-center"
            >
              <span className="mr-2">{log.icon}</span>
              <span className={
                log.message.includes('Critical') ? 'text-red-400' :
                log.message.includes('Alert') ? 'text-yellow-400' :
                'text-gray-100'
              }>
                {log.message}
              </span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default ScanAnimation;